import React from "react";
import moment from "moment";

const TripDetailsModal = ({ trip, onClose }) => {
  if (!trip) return null;

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return moment(timestamp).format("MMM DD, YYYY h:mm A");
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    return moment(timestamp).format("HH:mm");
  };

  const stepType = trip.type || trip.StepType || "Unknown";
  const isPickup = stepType.toLowerCase() === "pickup";
  const isDelivery = stepType.toLowerCase() === "delivery" || stepType.toLowerCase() === "dropoff";
  const isBreak = trip.isBreak || trip.breakStep;
  const isStart = trip.beginStep || stepType.toLowerCase() === "start";
  const isEnd = trip.endStep || stepType.toLowerCase() === "end";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2200] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-800">
            Trip #{trip.tripNo || trip.TripNo}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="px-4 py-2 space-y-2">
          {/* Break Information */}
          {isBreak && (
            <div className="bg-blue-50 rounded-lg p-2">
              <h3 className="font-semibold text-gray-700 mb-1 text-base">Break Period</h3>
              <div className="text-base">
                <div>
                  <span className="text-gray-600">Time:</span>
                  <span className="ml-2 font-medium">{trip.startTime} - {trip.endTime}</span>
                </div>
                <div className="mt-1">
                  <span className="text-gray-600">Duration:</span>
                  <span className="ml-2 font-medium">{trip.breakTime || 0} minutes</span>
                </div>
              </div>
            </div>
          )}

          {/* Start/End Step Information */}
          {(isStart || isEnd) && (
            <div className={`rounded-lg p-2 ${isStart ? "bg-green-50" : "bg-red-50"}`}>
              <h3 className="font-semibold text-gray-700 mb-1 text-base">{isStart ? "Route Start" : "Route End"}</h3>
              <div className="text-base">
                <div>
                  <span className="text-gray-600">Time:</span>
                  <span className="ml-2 font-medium">{trip.scheduledTime || formatTime(trip.StartDateTime)}</span>
                </div>
                {trip.address && (
                  <div className="mt-1">
                    <span className="text-gray-600">Location:</span>
                    <span className="ml-2 font-medium">{trip.address}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Passenger Information */}
          {trip.trip?.passenger && !isBreak && !isStart && !isEnd && (
            <div className="bg-gray-50 rounded-lg p-2">
              <h3 className="font-semibold text-gray-700 mb-1 text-base">Passenger</h3>
              <div className="space-y-0.5 text-base">
                <div className="font-medium">{trip.passengerName || trip.trip.passenger.name}</div>
                {trip.trip.passenger.account && (
                  <div>{trip.trip.passenger.account}</div>
                )}
                {trip.trip.reservation && (
                  <>
                    {(trip.trip.reservation.wheelchair > 0 || trip.wheelChair) && (
                      <div>Wheelchair</div>
                    )}
                    {trip.trip.reservation.ambulatory > 0 && (
                      <div>Ambulatory ({trip.trip.reservation.ambulatory})</div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Pickup Information */}
          {trip.trip?.pickup && (isPickup || isDelivery) && (
            <div className="bg-yellow-50 rounded-lg p-2">
              <h3 className="font-semibold text-gray-700 mb-1 text-base">Pickup</h3>
              <div className="space-y-0.5 text-base">
                {trip.trip.pickup.landmark && (
                  <div className="font-medium">{trip.trip.pickup.landmark}</div>
                )}
                <div className="font-medium">{trip.trip.pickup.address}</div>
                {trip.trip.pickup.date && (
                  <div>
                    {formatTime(trip.trip.pickup.date)}
                    {trip.time && isPickup && (
                      <span className="ml-2 text-gray-600">
                        (ETA: {moment(trip.time, "hh:mm:ss A").format("HH:mm")})
                      </span>
                    )}
                  </div>
                )}
                {trip.trip.pickup.comments && (
                  <div className="text-sm text-gray-600 mt-1 italic">{trip.trip.pickup.comments}</div>
                )}
              </div>
            </div>
          )}

          {/* Dropoff Information */}
          {trip.trip?.dropoff && (isPickup || isDelivery) && (
            <div className="bg-blue-50 rounded-lg p-2">
              <h3 className="font-semibold text-gray-700 mb-1 text-base">Dropoff</h3>
              <div className="space-y-0.5 text-base">
                {trip.trip.dropoff.landmark && (
                  <div className="font-medium">{trip.trip.dropoff.landmark}</div>
                )}
                <div className="font-medium">{trip.trip.dropoff.address}</div>
                {trip.trip.dropoff.date && (
                  <div>
                    {formatTime(trip.trip.dropoff.date)}
                    {trip.time && isDelivery && (
                      <span className="ml-2 text-gray-600">
                        (ETA: {moment(trip.time, "hh:mm:ss A").format("HH:mm")})
                      </span>
                    )}
                  </div>
                )}
                {trip.trip.dropoff.comments && (
                  <div className="text-sm text-gray-600 mt-1 italic">{trip.trip.dropoff.comments}</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-4 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripDetailsModal;
